import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { apiClient } from "../../../../../api/httpClient";

/** Same error normaliser the single-loser dialog uses. */
const formatApiError = (err) => {
  if (err == null) return "Request failed";
  if (typeof err === "string") {
    if (err.includes("Server Error") || err.includes("<!DOCTYPE") || err.includes("<html")) {
      return (
        "Server error (500). The API returned an HTML error page — check the Django " +
        "server logs on the environment you are calling. Common causes: missing table " +
        "Vermost_Account_Merge_Log, a SQL error during merge, or an unhandled exception in " +
        "account_merge."
      );
    }
    return err;
  }
  if (Array.isArray(err.detail)) {
    return err.detail
      .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
      .join("; ");
  }
  if (err.detail != null) return String(err.detail);
  if (err.message) return String(err.message);
  return "Request failed";
};

/**
 * Confirmation dialog for merging every active duplicate in a group into the
 * chosen keeper. Posts to ``/duplicate-accounts/bulk-merge`` with all pairs.
 *
 * Per-pair backend transactions mean partial success is possible — surfaced
 * as a per-row result list so the caller can re-run failed merges.
 */
const MergeAllDuplicatesDialog = ({
  open,
  onClose,
  mergeType,
  keeperId,
  keeperLabel,
  duplicates,
  onMerged,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [result, setResult] = useState(null);

  const reset = () => {
    setSubmitError(null);
    setResult(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleConfirm = async () => {
    if (!keeperId || !duplicates?.length) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const data = await apiClient(
        "/duplicate-accounts/bulk-merge",
        "POST",
        {
          merge_type: mergeType,
          pairs: duplicates.map((d) => ({ keeper_id: keeperId, loser_id: d.id })),
        }
      );
      setResult(data);
      if (onMerged) onMerged(data);
    } catch (err) {
      setSubmitError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const dupCount = duplicates?.length || 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Merge {dupCount} duplicate{dupCount === 1 ? "" : "s"} into keeper
      </DialogTitle>
      <DialogContent dividers>
        {result ? (
          <Box>
            <Alert
              severity={result.failure_count === 0 ? "success" : "warning"}
              sx={{ mb: 2 }}
            >
              {result.success_count} succeeded
              {result.failure_count > 0 ? `, ${result.failure_count} failed` : ""}
              {` (of ${result.total}).`}
            </Alert>
            {result.failure_count > 0 ? (
              <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Failures</Typography>
                {result.results
                  .filter((r) => !r.success)
                  .map((r, i) => (
                    <Typography
                      key={`${r.keeper_id}-${r.loser_id}-${i}`}
                      variant="caption"
                      component="div"
                      sx={{ color: "error.main" }}
                    >
                      loser={r.loser_id}: {r.error}
                    </Typography>
                  ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                All duplicates were merged into the keeper account.
              </Typography>
            )}
          </Box>
        ) : (
          <Box>
            {submitError ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            ) : null}
            <Alert severity="warning" sx={{ mb: 2 }}>
              Every reference from each duplicate is reassigned to the <strong>keeper</strong>,
              then each duplicate is soft-deleted. Each merge is a separate transaction and is
              individually reversible from the Merge Log tab within 30 days.
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>KEEPER</Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {keeperLabel || `ID ${keeperId}`}
              </Typography>
            </Box>

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Duplicates to merge ({dupCount})
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
              {duplicates.map((d) => (
                <Chip
                  key={d.id}
                  size="small"
                  variant="outlined"
                  label={`${d.label} — ID ${d.id}`}
                />
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        {result ? (
          <Button variant="contained" onClick={handleClose}>Done</Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>Cancel</Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              disabled={submitting || dupCount === 0}
            >
              {submitting ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Merging…
                </>
              ) : (
                `Confirm merge (${dupCount})`
              )}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

MergeAllDuplicatesDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  mergeType: PropTypes.oneOf(["portal_user", "law_firm", "professional_guardian"]).isRequired,
  keeperId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  keeperLabel: PropTypes.string,
  duplicates: PropTypes.arrayOf(
    PropTypes.shape({
      loser_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  onMerged: PropTypes.func,
};

export default MergeAllDuplicatesDialog;
